/* eslint-disable prettier/prettier */
import { Box, Typography } from "@mui/material";
import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "../../components/common/Button";
import CustomAutoComplete from "../../components/CustomAutoComplete";
import {
  MeetingTypes,
  RegionMeetingTypes,
  OtherMeetingTypes,
} from "../../contants";
import { SET_MEETING_TYPE } from "../../redux/reducers/attendance";
import Styles from "./styles";
import AutoCompleteDropdown from "../../components/common/new/AutoCompleteDropdown/AutoCompleteDropdown";
import { getCurrentMonth, getCurrentYear } from "../../utils/dateAndTime";
import { selectAllSIUsers } from "../../redux/selectors/user";
import { selectAttendanceMeetingType } from "../../redux/selectors/attendance";

function AttendanceForMobile() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userProfile = useSelector((state) => state.auth?.userProfile?.data);

  const selectedMeetingType = useSelector(selectAttendanceMeetingType);
  const { isRegionSi } = useSelector(selectAllSIUsers);

  // ✅ desktop jaisa: dropdown value = option object
  const [meetinType, setMeetingType] = React.useState(null);
  const [otherMeetingType, setOtherMeetingType] = React.useState(null);

  const isDistrictSI = Boolean(userProfile && userProfile.districtSi);

  const onClickForward = () => {
    navigate("/attendance");
  };

  const onSelectMeeting = (selectedValue) => {
    setMeetingType(selectedValue || null);

    // ✅ mutually exclusive
    setOtherMeetingType(null);

    dispatch({
      type: SET_MEETING_TYPE,
      payload: {
        type: selectedValue?.value ?? "",
      },
    });
  };

  const onSelectOtherMeeting = (selectedValue) => {
    setOtherMeetingType(selectedValue || null);

    // ✅ mutually exclusive
    setMeetingType(null);

    dispatch({
      type: SET_MEETING_TYPE,
      payload: {
        type: selectedValue?.value ?? "",
      },
    });
  };

  // ✅ desktop jaisa: redux -> dropdown sync (persist selection after render)
  React.useEffect(() => {
    // if current selected is in MeetingTypes/RegionMeetingTypes => set meeting dropdown
    const meetingOptions = isRegionSi ? RegionMeetingTypes : MeetingTypes;
    const selectedMeetingOpt =
      meetingOptions?.find((o) => o.value === selectedMeetingType) || null;

    // if current selected is in OtherMeetingTypes => set other dropdown
    const selectedOtherOpt =
      OtherMeetingTypes?.find((o) => o.value === selectedMeetingType) || null;

    if (selectedMeetingOpt) {
      setMeetingType(selectedMeetingOpt);
      setOtherMeetingType(null);
    } else if (selectedOtherOpt) {
      setOtherMeetingType(selectedOtherOpt);
      setMeetingType(null);
    } else {
      // fallback: first time default (same intent as old mobile)
      const defaultType = isRegionSi ? "CUBS_MEETING" : "DISCUSSION_MEETING";
      const defaultOpt =
        meetingOptions?.find((o) => o.value === defaultType) || null;

      setMeetingType(defaultOpt);
      setOtherMeetingType(null);

      dispatch({
        type: SET_MEETING_TYPE,
        payload: { type: defaultType },
      });
    }
  }, [selectedMeetingType, isRegionSi]); // keep stable

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: "0px 16px",
        minHeight: "900px",
        overflowY: "scroll",
      }}
    >
      {/* Organization Structure */}
      <Box sx={{ mb: "16px" }}>
        <Typography style={{ ...Styles.typography, marginBottom: "8px" }}>
          Organization Structure
        </Typography>

        <Box
          sx={{
            border: "1px solid #C4C4C4",
            borderRadius: "8px",
            padding: "16px",
          }}
        >
          <Box sx={{ mb: "16px" }}>
            <CustomAutoComplete
              required
              name="Area"
              label="Area"
              disabled
              options={[]}
              optionLabelId="name"
              optionValueId="structureId"
              selectedValue={() => {}}
              valueOnDisabled={userProfile?.areaName}
            />
          </Box>

          <Box sx={{ mb: "16px" }}>
            <CustomAutoComplete
              required
              name="Zone"
              label="Zone"
              disabled
              options={[]}
              optionLabelId="name"
              optionValueId="structureId"
              selectedValue={() => {}}
              valueOnDisabled={userProfile?.zoneName}
            />
          </Box>

          <Box sx={{ mb: "16px" }}>
            <CustomAutoComplete
              required
              name="Region"
              label="Region"
              disabled
              options={[]}
              optionLabelId="name"
              optionValueId="structureId"
              selectedValue={() => {}}
              valueOnDisabled={userProfile?.regionName}
            />
          </Box>

          {!isRegionSi && (
            <>
              <Box sx={{ mb: "16px" }}>
                <CustomAutoComplete
                  required
                  name="Chapter"
                  label="Chapter"
                  disabled
                  options={[]}
                  optionLabelId="name"
                  optionValueId="structureId"
                  selectedValue={() => {}}
                  valueOnDisabled={userProfile?.chapterName}
                />
              </Box>
              <Box sx={{ mb: "0px" }}>
                <CustomAutoComplete
                  required
                  name="District"
                  label="District"
                  disabled
                  options={[]}
                  optionLabelId="name"
                  optionValueId="structureId"
                  selectedValue={() => {}}
                  valueOnDisabled={userProfile?.districtName}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Meetings Card */}
      <Box
        sx={{
          border: "1px solid #E0E0E0",
          borderRadius: "8px",
          padding: "16px",
        }}
      >
        {/* Month/Year */}
        <Box
          sx={{
            border: "1px solid #E0E0E0",
            borderRadius: "6px",
            padding: "10px 16px",
            mb: "16px",
            display: "flex",
            gap: "30px",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontWeight: "700",
                fontSize: "11px",
                color: "#8F92A1",
                mb: "4px",
              }}
            >
              Month
            </Typography>
            <Typography
              sx={{
                fontWeight: "500",
                fontSize: "14px",
                color: "#171717",
              }}
            >
              {getCurrentMonth()}
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                fontWeight: "700",
                fontSize: "11px",
                color: "#8F92A1",
                mb: "4px",
              }}
            >
              Year
            </Typography>
            <Typography
              sx={{
                fontWeight: "500",
                fontSize: "14px",
                color: "#171717",
              }}
            >
              {getCurrentYear()}
            </Typography>
          </Box>
        </Box>

        {/* Mark Attendance Section */}
        <Box
          sx={{
            border: "1px solid #E0E0E0",
            borderRadius: "8px",
            padding: "16px",
            mb: "16px",
          }}
        >
          <Typography
            sx={{
              fontWeight: "600",
              fontSize: "15px",
              color: "#171717",
              mb: "12px",
            }}
          >
            Mark Attendance
          </Typography>

          <Box sx={{ mb: "12px" }}>
            <AutoCompleteDropdown
              getOptionLabel={(option) => option.displayName}
              name="MeetingType"
              label={isRegionSi ? "Select Meeting" : "Select Discussion/Gosho"}
              options={isRegionSi ? RegionMeetingTypes : MeetingTypes}
              onChange={onSelectMeeting}
              value={meetinType}
              style={{ fontSize: "14px" }}
              disabled={!isDistrictSI && !isRegionSi}
              inputProps={{
                required: true,
                placeholder: "Select meeting type",
              }}
            />
          </Box>

          <Button
            variant="contained"
            label="Mark Attendance"
            onClick={onClickForward}
            disabled={!meetinType?.value}
            style={
              !meetinType?.value
                ? { opacity: ".5", width: "100%", pointerEvents: "none" }
                : { width: "100%" }
            }
          />
        </Box>

        {/* Other Meeting Section */}
        {!isRegionSi && (
          <Box
            sx={{
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
              padding: "16px",
            }}
          >
            <Typography
              sx={{
                fontWeight: "600",
                fontSize: "15px",
                color: "#171717",
                mb: "12px",
              }}
            >
              Other Meeting
            </Typography>

            <Box sx={{ mb: "12px" }}>
              <AutoCompleteDropdown
                getOptionLabel={(option) => option.displayName}
                name="OtherMeetingType"
                label="Select Other Meeting Type"
                options={OtherMeetingTypes}
                onChange={onSelectOtherMeeting}
                value={otherMeetingType}
                style={{ fontSize: "14px" }}
                disabled={!isDistrictSI && !isRegionSi}
                inputProps={{
                  required: true,
                  placeholder: "Select other meeting type",
                }}
              />
            </Box>

            <Button
              variant="contained"
              label="Mark   Attendance"
              onClick={onClickForward}
              disabled={!otherMeetingType?.value}
              style={
                !otherMeetingType?.value
                  ? {
                      opacity: ".5",
                      width: "100%",
                      pointerEvents: "none",
                      whiteSpace: "normal",
                    }
                  : { width: "100%" }
              }
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default AttendanceForMobile;
